/**
 * Layout with Ant Design - Combined Sidebar + Header layout
 */
import { ReactElement, useState } from 'react'
import { Layout, Menu, Flex, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import {
  SearchOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { logout } from 'actions/auth'
import { getUsername } from 'selectors/auth'
import styles from './Layout.module.scss'
import logo from 'style/img/virtuallog.svg'
import version from 'lib/version'

const { Sider, Header, Content } = Layout

interface ReduxState {
  username: string | null
}

const select = (): ReduxState => ({
  username: getUsername(),
})

const AppLayout = (props: ReduxState): ReactElement => {
  const { username } = props
  const [collapsed, setCollapsed] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  const menuItems = [
    {
      key: '/',
      icon: <SearchOutlined />,
      label: 'Search',
      onClick: () => navigate('/'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
  ]

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'username',
      label: username || 'User',
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => logout(),
    },
  ]

  return (
    <Layout className={styles.layout}>
      {/* Header spanning full width */}
      <Header className={styles.header}>
        <Flex
          justify="space-between"
          align="center"
          className={styles.headerContent}
        >
          {/* Logo on the left */}
          <Flex align="center">
            <img src={logo} alt="VirtualLog" className={styles.logo} />
          </Flex>

          {/* Right side: Profile dropdown */}
          <Flex align="center" gap={16}>
            <Dropdown menu={{ items: profileMenuItems }} trigger={['click']}>
              <div className={styles.profileIcon}>
                <UserOutlined style={{ fontSize: '20px', cursor: 'pointer' }} />
              </div>
            </Dropdown>
          </Flex>
        </Flex>
      </Header>

      {/* Bottom section with Sidebar + Content */}
      <Layout>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          className={styles.sider}
          data-tour="sidebar-nav"
        >
          {/* Navigation Menu */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            className={styles.menu}
          />

          {/* Version at the bottom */}
          <div className={styles.version}>v{version}</div>
        </Sider>

        <Content className={styles.content}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default connect(select)(AppLayout)
